import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { PlanResource } from "../data/models/Plan/PlanModel";
import { useAdminPlansPage } from "./hooks/useAdminPlansPage";
import { useUploadResource } from "./hooks/useUploadResource";

export default function AdminPlansPage() {
  const { uploadResource, uploading, progress } = useUploadResource();
  const { plans, loading, pushNewPlanResource, deletePlanResource } =
    useAdminPlansPage();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedResource, setSelectedResource] = useState<any>(null);

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography variant="h6" color="text.secondary">
          Cargando planes...
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {(plans ?? []).map((plan) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={plan.code}>
            <Card
              sx={{
                borderRadius: 4,
                p: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease-in-out",
                "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.08)" },
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {plan.name}
                    </Typography>
                  </Box>

                  <Chip
                    label={`$${plan.priceUsd}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  mt={3}
                  mb={1}
                  color="text.primary"
                >
                  {plan.description}
                </Typography>

                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  mt={3}
                  mb={1}
                  color="text.primary"
                >
                  Capacidades del plan
                </Typography>

                {(Array.isArray(plan.capacities) ? plan.capacities : []).map((cap) => (
                  <Box
                    key={cap.id}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 3,
                      p: 2,
                      mb: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      {cap.capacityName}
                    </Typography>

                    <Stack spacing={1}>
                      {(Array.isArray(cap.resources) ? cap.resources : []).map((r, idx) => (
                        <Stack
                          key={r.id || idx}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          {r.resourceType === "VIDEO" ? (
                            <VideoIcon fontSize="small" color="action" />
                          ) : r.resourceType === "PDF" ? (
                            <DescriptionIcon fontSize="small" color="action" />
                          ) : (
                            <LinkIcon fontSize="small" color="action" />
                          )}

                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {r.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", wordBreak: "break-all" }}
                            >
                              {r.url}
                            </Typography>
                          </Box>

                          <Tooltip title="Eliminar recurso">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedResource(r);
                                setOpenConfirm(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          borderRadius: 2,
                        }}
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/*,video/*,application/pdf";
                          input.onchange = async (e: any) => {
                            const file = e.target.files[0];
                            if (!file) return;

                            try {
                              const newResource = (await uploadResource(
                                file,
                                cap.id
                              )) as PlanResource;
                              pushNewPlanResource(newResource);
                            } catch (err) {
                              console.error("Error al subir recurso:", err);
                            }
                          };
                          input.click();
                        }}
                        disabled={uploading}
                      >
                        {uploading
                          ? `Subiendo ${progress.toFixed(0)}%...`
                          : "Agregar recurso"}
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ConfirmationDialog
        open={openConfirm}
        title="Eliminar recurso"
        message={`¿Seguro que deseas eliminar "${selectedResource?.title}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onClose={() => setOpenConfirm(false)}
        onConfirm={async () => {
          if (selectedResource) {
            await deletePlanResource(selectedResource);
          }
          setOpenConfirm(false);
        }}
      />
    </Box>
  );
}
